import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertTriangle, Calendar, ChevronDown, Info, List, CalendarClock } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { StepProps } from "@/lib/builder/models/builder.model";
import { SaveToLibraryModal } from "./modals/SaveToLibraryModal";
import { AccommodationCard } from "./activities-card/AccommodationCard";
import { ActivityCard } from "./activities-card/ActivityCard";
import { FlightCard } from "./activities-card/FlightCard";
import { TransportationCard } from "./activities-card/TransportationCard";
import { FoodDrinksCard } from "./activities-card/FoodDrinksCard";
import { EventsCard } from "./activities-card/EventsCard";
import { NotesCard } from "./activities-card/NotesCard";
import { useModalCallbacks } from "./hooks/useModalCallbacks";
import { ItineraryBuilder, ItineraryDayBuilder } from "./models/itinerary-builder.model";
import { activityDaySpan, axisFromItinerary, dayAnchorForActivity, dayKeyFromActivity } from "./models/day-axis";
import { ItineraryInsights } from "./components/ItineraryInsights";
import { formatDayTitle, formatDayWeekday } from "./components/day-labels";
import { DayInsights } from "./components/DayInsights";
import {
  ActivityType,
  AccommodationActivity,
  ActivityActivity,
  FlightActivity,
  TransportationActivity,
  FoodDrinksActivity,
  EventsActivity,
  NotesActivity,
  ActivityFactory,
  ItineraryActivity,
  AccommodationActivityClass,
  SupplierStatus
} from "./models/activity.model";
import { ACTIVITIES } from "./models/activity-configs.model";
import { ActivityTypePicker } from "./components/ActivityTypePicker";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";
import { TimelineCard } from "./components/TimelineCard";
import { getActivityTime, getSortedActivities } from "./components/timeline-utils";
import React from 'react';

const DayBuilderStep = ({
  data,
  onDataChange,
  canProceed,
  setCanProceed,
  selectedDate,
  isReadOnly = false,
  isPricingLocked = false,
  isTraveler = false,
  onBuilderChange,
  failedActivityIds,
  onActivityReorder,
}: StepProps) => {
  // Inizializza il builder dall'itinerario esistente
  const { userRole, user } = useAuth();
  const { t, language } = useI18n();
  const { toast } = useToast();

  const builder = useMemo(() => {
    const itineraryBuilder = new ItineraryBuilder();

    // L'asse è dato dalle date (itinerario collegato) o dalla durata (template).
    const axis = data ? axisFromItinerary(data) : null;

    if (axis) {
      if (data.itinerary_days && data.itinerary_days.length > 0) {
        // Convert raw activity objects into typed activity instances via ActivityFactory
        const normalizedDays = data.itinerary_days.map((d: any) => ({
          key: d.key,
          date: d.date,
          activities: (d.activities || []).map((a: any) => ActivityFactory.create(a as ItineraryActivity))
        }));

        itineraryBuilder.loadFromArray(normalizedDays, axis);
      } else {
        // Nessuna attività ancora, ma genera comunque i giorni vuoti
        itineraryBuilder.initializeDays(axis);
      }

      // I giorni fuori dalla finestra della richiesta restano visibili, ma segnalati.
      itineraryBuilder.setTravelRequestWindow(
        data.travel_request?.travel_start_date,
        data.travel_request?.travel_end_date,
      );
    } else {
      console.warn('⚠️ No axis available in data');
    }

    return itineraryBuilder;
  }, [data]);

  // Notify parent about builder changes
  useEffect(() => {
    if (onBuilderChange) {
      onBuilderChange(builder);
    }
  }, [builder, onBuilderChange]);

  const [currentDay, setCurrentDay] = useState<ItineraryDayBuilder | null>(null);
  const [, forceUpdate] = useState({});

  // Vista lato cliente: nasconde i campi riservati al travel advisor (costo del singolo
  // travel object, link fornitore, stato fornitore). Due sorgenti, entrambe necessarie:
  //  - `userRole === 'traveler'` copre il cliente autenticato nel builder;
  //  - la prop `isTraveler` copre la proposta condivisa e il viewer offline, dove
  //    l'utente è anonimo e `userRole` è null.
  // Prima le card leggevano solo il ruolo e la TimelineCard solo la prop: ognuna delle
  // due viste finiva per mostrare il costo tramite l'altra strada.
  const isClientView = isTraveler || userRole === 'traveler';

  const [viewMode, setViewMode] = useState<'list' | 'timeline'>(isReadOnly || isClientView ? 'timeline' : 'list');

  // Attività in attesa di conferma tag nel SaveToLibraryModal (null = modal chiuso).
  const [activityToSaveToLibrary, setActivityToSaveToLibrary] = useState<ItineraryActivity | null>(null);

  // Aggiorna il giorno corrente quando cambia il giorno selezionato o il builder
  useEffect(() => {
    if (selectedDate) {
      setCurrentDay(builder.getDay(selectedDate) || null);
    }
  }, [selectedDate, builder]);

  // Helper function per aggiornare il data model e forzare re-render
  const updateBuilder = () => {
    // Forza re-render
    forceUpdate({});
    
    // Aggiorna currentDay
    if (selectedDate) {
      setCurrentDay(builder.getDay(selectedDate) || null);
    }
    
    // Notifica il cambio al parent
    onDataChange({ itinerary_days: builder.toArray() });
  };

  // Function to add or update activity
  const addActivities = (activityData: ItineraryActivity, isEditing?: boolean, editingActivityId?: string) => {
    if (!selectedDate) return;

    // Se è editing, rimuovi l'attività esistente
    if (isEditing && editingActivityId) {
      builder.removeActivityById(editingActivityId);
    }

    // Giorno su cui atterra l'attività: quello scelto nella modale o, in mancanza,
    // il giorno selezionato nel builder.
    const axis = builder.axis;
    const dayKey = (axis && dayKeyFromActivity(axis, activityData)) || selectedDate;

    // L'ancoraggio (data reale o intervallo di giorni) va scritto sull'attività prima di
    // crearla: le due forme si escludono a vicenda e il DB rifiuta le attività senza nessuna
    // delle due. La durata che l'attività si porta dietro va preservata, altrimenti un
    // soggiorno di più giorni verrebbe schiacciato sul solo giorno di partenza.
    const activityDataWithDate = {
      ...activityData,
      ...(axis
        ? dayAnchorForActivity(axis, dayKey, activityDaySpan(activityData))
        : { date: dayKey }),
    };

    // Crea l'attività tipizzata usando ActivityFactory
    const activity = ActivityFactory.create(activityDataWithDate as ItineraryActivity);

    // GESTIONE ACCOMMODATION
    if (activity instanceof AccommodationActivityClass || activityData.type === 'accommodation') {
      const [checkIn, checkOut] = createCheckInOutActivities(activity as AccommodationActivityClass);

      // Check-in e check-out entrano insieme: la copertura si ricalcola una volta sola,
      // e su uno stato che contiene già entrambe le voci.
      const checkOutKey = (axis && dayKeyFromActivity(axis, { date: checkOut.date, start_day_index: checkOut.start_day_index })) || dayKey;
      builder.addActivities([
        [dayKey, ActivityFactory.create(checkIn as ItineraryActivity) as ItineraryActivity],
        [checkOutKey, ActivityFactory.create(checkOut as ItineraryActivity) as ItineraryActivity],
      ]);
    } else {
      // Aggiungi attività normale
      builder.addActivity(dayKey, activity as ItineraryActivity);
    }

    updateBuilder();
  };

  // derive itinerary bounds from builder
  const itineraryStartDate = builder.startDate;
  const itineraryEndDate = builder.endDate;

  const modalCallbacks = useModalCallbacks({
    onSave: addActivities,
    itineraryStartDate,
    itineraryEndDate,
    axis: builder.axis,
    itineraryId: data?.id || '',
    destinations: data?.travel_request?.destinations,
    guestsData: data?.travel_request?.guests_data as import("@/lib/request-trip/models/travel-request.model").IGuest[] | undefined,
    totalGuests: data?.travel_request?.total_guests,
    selectedDate: selectedDate || undefined,
    isPricingLocked,
  });

  const [isDragging, setIsDragging] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragStartTimer = useRef<number | null>(null);

  // Listen to global drag events to detect when the user drags out of the window
  // (failed drop) so we can hide external drop zones and clear timers/placeholders.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onWindowDragLeave = (e: DragEvent) => {
      // When the drag leaves the window most browsers emit a dragleave with pageX/Y === 0
      // Use that as a signal that the user dragged outside the app (likely a failed drop).
      // Clear any pending timers and reset drag UI state.
      try {
        // pageX/pageY may be present on DragEvent
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (e.pageX === 0 && e.pageY === 0) {
          if (dragStartTimer.current) {
            window.clearTimeout(dragStartTimer.current);
            dragStartTimer.current = null;
          }
          setIsDragging(false);
          setDraggingId(null);
        }
      } catch (err) {
        // swallow any unexpected errors - recovery is simple (resetting state)
        if (dragStartTimer.current) {
          window.clearTimeout(dragStartTimer.current);
          dragStartTimer.current = null;
        }
        setIsDragging(false);
        setDraggingId(null);
      }
    };

    const onWindowDropOrEnd = () => {
      if (dragStartTimer.current) {
        window.clearTimeout(dragStartTimer.current);
        dragStartTimer.current = null;
      }
      setIsDragging(false);
      setDraggingId(null);
    };

    window.addEventListener('dragleave', onWindowDragLeave);
    // Listen to 'drop' and 'dragend' globally as a fallback when dropping outside
    window.addEventListener('drop', onWindowDropOrEnd);
    window.addEventListener('dragend', onWindowDropOrEnd);

    return () => {
      window.removeEventListener('dragleave', onWindowDragLeave);
      window.removeEventListener('drop', onWindowDropOrEnd);
      window.removeEventListener('dragend', onWindowDropOrEnd);
    };
  }, []);

  // Update canProceed based on whether we have at least one activity
  useEffect(() => {
    const hasActivities = builder.isValid();
    setCanProceed(hasActivities);
  }, [builder, setCanProceed]);

  const handleAddActivity = (activityType: ActivityType) => {
    modalCallbacks.openModal(activityType);
  };

  const deleteActivity = (date: string, activity: ItineraryActivity) => {
    builder.removeActivityById(activity.id);
    updateBuilder();
  };

  // Stato fornitore: sempre visibile e modificabile dal TA/admin, in qualsiasi stato
  // dell'itinerario (il TA contatta i fornitori già in fase di proposta, non solo dopo
  // la prenotazione). Il traveler/cliente non lo vede mai: escluso sia dal ruolo che
  // dalla modalità read-only (usata anche dalla proposta condivisa pubblica, dove
  // l'utente è anonimo e userRole è null).
  const showSupplierStatus = !isReadOnly && !isClientView;

  const handleSupplierStatusChange = (activity: ItineraryActivity, status: SupplierStatus) => {
    const found = builder.findActivityById(activity.id);
    if (!found) return;
    found.activity.supplier_status = status;
    updateBuilder();
  };

  const duplicateActivity = (date: string, activity: ItineraryActivity) => {
    const day = builder.getDay(date);
    if (!day) return;

    const activityToDuplicate = day.findActivity(activity.id);
    if (!activityToDuplicate) return;

    const duplicatedActivity = ActivityFactory.create({
      ...activityToDuplicate,
      id: crypto.randomUUID(),
      title: `${activityToDuplicate.title} (Copy)`
    } as ItineraryActivity);

    builder.addActivity(date, duplicatedActivity as ItineraryActivity);
    updateBuilder();
  };

  // Apre il SaveToLibraryModal per scegliere i tag prima di salvare l'attività.
  // Il salvataggio vero e proprio (e l'ambito personale/agenzia, ora implicito in
  // base al ruolo — vedi SaveToLibraryModal / useLibraryScope) avviene nel modal,
  // alla conferma dell'utente.
  const handleSaveToLibrary = (activity: ItineraryActivity) => {
    if (!user?.id) {
      console.error('Cannot save activity to library: missing current user id');
      toast({
        title: t('common.error'),
        description: t('library.saveErrorDescription'),
        variant: 'destructive',
      });
      return;
    }

    setActivityToSaveToLibrary(activity);
  };

  // Su un template non ci sono date: il giorno si chiama con la sua posizione.
  const locale = language || 'it-IT';

  const formatTitle = (day: ItineraryDayBuilder) =>
    formatDayTitle(day, { locale, month: 'long', fallback: number => t('builder.dayNumber', { number }) });

  const formatDescription = (day: ItineraryDayBuilder) => formatDayWeekday(day, locale);

  // External drop zone rendered between cards and at the start/end of the list
  const DropZone = ({ onDrop, visible }: { onDrop: (sourceId: string) => void; visible: boolean }) => {
    const [hover, setHover] = React.useState(false);
    // Render the drop zone but hide it when not dragging
    return (
      <div
        className={`w-full h-6 my-0.5 rounded-md border-2 transition-colors flex items-center justify-center ${visible ? (hover ? 'border-sky-400 bg-sky-50' : 'border-dashed border-gray-200 bg-transparent') : 'hidden'}`}
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragEnter={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHover(false);
          const sourceId = e.dataTransfer.getData('text/plain');
          if (sourceId) onDrop(sourceId);
        }}
      >
        {/* visual hint when hovering */}
        {hover ? <span className="text-sm text-sky-600">{t('builder.dropHere')}</span> : null}
      </div>
    );
  };

  // Function to render the correct card based on activity type
  const renderActivityCard = (activity: ItineraryActivity) => {
    const commonProps = {
      onEdit: (activity: ItineraryActivity) => {
        modalCallbacks.openModal(activity.type, undefined, activity);
      },
      onDelete: (activity: ItineraryActivity) => deleteActivity(currentDay!.key, activity),
      onDuplicate: (activity: ItineraryActivity) => duplicateActivity(currentDay!.key, activity),
      onSaveToLibrary: handleSaveToLibrary,
      onExpand: () => { },
      isReadOnly,
      isTraveler: isClientView,
      // `isAdmin` scavalca il filtro sui campi riservati (shouldShowCost): va concesso
      // solo quando l'admin sta guardando il builder, non quando apre un link condiviso.
      isAdmin: userRole === 'admin' && !isClientView,
      unsaved: failedActivityIds?.has(activity.id) ?? false,
      showSupplierStatus,
      onSupplierStatusChange: handleSupplierStatusChange,
      // drag handlers
        onDragStart: (act: ItineraryActivity) => {
          // Delay showing drop zones and setting the placeholder id slightly to avoid
          // removing the dragged element from the DOM before the native drag image
          // is captured (which can cancel the drag). We set both inside the same
          // short timer so the dragstart can complete first.
          if (dragStartTimer.current) window.clearTimeout(dragStartTimer.current);
          dragStartTimer.current = window.setTimeout(() => {
            setIsDragging(true);
            setDraggingId(act.id);
            dragStartTimer.current = null;
          }, 0);
        },
        onDragEnd: () => {
          // Cancel pending timer if any, and hide drop zones
          if (dragStartTimer.current) {
            window.clearTimeout(dragStartTimer.current);
            dragStartTimer.current = null;
          }
          setIsDragging(false);
          setDraggingId(null);
        },
        onDropBefore: (sourceId: string, targetId: string | undefined) => {
          console.log('drop', sourceId, 'before', targetId, 'on day', currentDay?.key);
          if (!currentDay) return;
          const sourceDate = builder.findActivityById(sourceId)?.key;
          const moved = builder.moveActivityBefore(sourceId, currentDay.key, targetId as any);
          if (moved) {
            const affectedDates = Array.from(new Set([currentDay.key, sourceDate].filter(Boolean) as string[]));
            onActivityReorder?.(affectedDates);
            updateBuilder();
          }
        }
    };

    switch (activity.type) {
      case 'accommodation':
        return <AccommodationCard activity={activity as AccommodationActivity} {...commonProps} />;
      case 'activity':
        return <ActivityCard activity={activity as ActivityActivity} {...commonProps} />;
      case 'flight':
        return <FlightCard activity={activity as FlightActivity} {...commonProps} />;
      case 'transportation':
        return <TransportationCard activity={activity as TransportationActivity} {...commonProps} />;
      case 'food-drinks':
        return <FoodDrinksCard activity={activity as FoodDrinksActivity} {...commonProps} />;
      case 'events':
        return <EventsCard activity={activity as EventsActivity} {...commonProps} />;
      case 'notes':
        return <NotesCard activity={activity as NotesActivity} {...commonProps} />;
      default:
        // Fallback to a generic card for unknown types
        return <ActivityCard activity={activity as ActivityActivity} {...commonProps} />;
    }
  };
  return (
    <>
      <div className="flex flex-col h-full">
        {currentDay ? (
          <>
            {/* Fixed Header Section */}
            <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-white">
              {/* Il giorno esce dalla finestra della richiesta: va detto qui, non solo nel menu. */}
              {currentDay.exceedsTravelRequest && (
                <Alert variant="warning" className="mb-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{t('builder.dayExceedsRequestDesc')}</AlertDescription>
                </Alert>
              )}

              {/* Day Header with Insights and Actions */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col items-start gap-1 flex-shrink-0 sm:min-w-[115px] min-w-[80px]">
                  <div className="text-sm">
                    {formatTitle(currentDay)}
                  </div>
                  <div className="text-md font-semibold capitalize">{formatDescription(currentDay)}</div>
                </div>

                {/* Day Insights Component - expands to fill available space */}
                <div className="flex-1 flex sm:justify-center justify-end items-center overflow-hidden">
                  <DayInsights day={currentDay} />
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* View Mode Toggle */}
                  <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="flex items-center gap-1 sm:gap-2">
                          {viewMode === 'timeline' ? <CalendarClock className="h-4 w-4" /> : <List className="h-4 w-4" />}
                          <span className="hidden sm:inline">{t('builder.view')}</span>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setViewMode('timeline')} className={viewMode === 'timeline' ? 'bg-accent' : ''}>
                          <CalendarClock className="h-4 w-4 mr-2" />
                          {t('builder.switchToTimelineView')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-accent' : ''}>
                          <List className="h-4 w-4 mr-2" />
                          {t('builder.switchToListView')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Itinerary Insights Modal Button */}
                  {/* <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Info className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('builder.itineraryOverview')}</DialogTitle>
                      </DialogHeader>
                      <ItineraryInsights builder={builder} />
                    </DialogContent>
                  </Dialog> */}

                  {/* Add Activity Dropdown - Only show if not read-only */}
                  {!isReadOnly && (
                    <ActivityTypePicker onSelect={handleAddActivity} />
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Activities List or Timeline */}
            <div className={viewMode === 'timeline' ? 'flex-1 overflow-x-auto overflow-y-hidden' : 'flex-1 overflow-y-auto p-6'}>
              {currentDay.activities.length > 0 ? (
                viewMode === 'timeline' ? (
                  // Timeline horizontal view
                  <div className="flex gap-4 p-6 h-full items-stretch">
                    {getSortedActivities(currentDay.activities, true).map((activity, index, array) => (
                      <div key={activity.id} className={index === array.length - 1 ? 'pr-6' : ''}>
                        <TimelineCard
                          activity={activity}
                          isTraveler={isClientView}
                          onEdit={!isReadOnly ? (a) => modalCallbacks.openModal(a.type, undefined, a) : undefined}
                          onDelete={!isReadOnly ? (a) => deleteActivity(currentDay!.key, a) : undefined}
                          onDuplicate={!isReadOnly ? (a) => duplicateActivity(currentDay!.key, a) : undefined}
                          onSaveToLibrary={!isReadOnly ? handleSaveToLibrary : undefined}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // Traditional list view
                  <div className="flex flex-col gap-2">
                    {/* External drop zone before the first item to allow inserting at top */}
                    <DropZone
                      key={`drop-start-${currentDay.key}`}
                      visible={isDragging && draggingId !== currentDay.activities[0]?.id}
                      onDrop={(sourceId) => {
                        if (!currentDay) return;
                        // Compute the first activity id that is NOT the source (to avoid using source as target)
                        const firstDifferent = currentDay.activities.find(a => a.id !== sourceId)?.id;
                        // If firstDifferent equals undefined and source was the only item, this is a no-op
                        if (!firstDifferent && currentDay.activities.length <= 1) {
                          setIsDragging(false);
                          return;
                        }
                        // If source is already before firstDifferent (i.e., is the first), no move needed
                        if (currentDay.activities[0]?.id === sourceId) {
                          setIsDragging(false);
                          return;
                        }

                        const sourceDate = builder.findActivityById(sourceId)?.key;
                        const moved = builder.moveActivityBefore(sourceId, currentDay.key, firstDifferent);
                        if (moved) {
                          const affectedDates = Array.from(new Set([currentDay.key, sourceDate].filter(Boolean) as string[]));
                          onActivityReorder?.(affectedDates);
                          updateBuilder();
                        }
                        setIsDragging(false);
                        setDraggingId(null);
                      }}
                    />
                    {getSortedActivities(currentDay.activities, isReadOnly).map((activity) => (
                    <div key={activity.id}>
                      { /* If this activity is the one being dragged, render a placeholder (100px height, full width) */ }
                      {draggingId === activity.id ? (
                        <div className="w-full h-[64px] bg-gray-100 rounded-md border-2 border-dashed border-gray-300 mb-2" />
                      ) : (
                        renderActivityCard(activity)
                      )}
                      {/* Drop zone after each card to append after this card */}
                      <DropZone
                        key={`drop-after-${activity.id}`}
                        visible={isDragging && draggingId !== activity.id}
                        onDrop={(sourceId) => {
                          if (!currentDay) return;
                          // move before the next activity id; if this is the last, append
                          const idx = currentDay.activities.findIndex(a => a.id === activity.id);
                          const next = currentDay.activities[idx + 1];
                          const targetId = next ? next.id : undefined;
                          // If target equals source, treat as no-op
                          if (targetId === sourceId) {
                            setIsDragging(false);
                            return;
                          }
                          const sourceDate = builder.findActivityById(sourceId)?.key;
                          const moved = builder.moveActivityBefore(sourceId, currentDay.key, targetId as any);
                          if (moved) {
                            const affectedDates = Array.from(new Set([currentDay.key, sourceDate].filter(Boolean) as string[]));
                            onActivityReorder?.(affectedDates);
                            updateBuilder();
                          }
                          setIsDragging(false);
                          setDraggingId(null);
                        }}
                      />
                    </div>
                  ))}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 min-h-[500px]">
                  <div className="bg-gray-100 rounded-full p-6 mb-6">
                    <Calendar className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg text-gray-700 mb-2">
                    {isReadOnly ? t('builder.emptyDayTitleReadOnly') : t('builder.emptyDayTitleEdit')}
                  </h3>
                  <p className="text-gray-500 text-center max-w-sm mb-6">
                    {isReadOnly
                      ? t('builder.emptyDayDescriptionReadOnly')
                      : t('builder.emptyDayDescriptionEdit')
                    }
                  </p>
                  {!isReadOnly && (
                    <div className="flex flex-wrap gap-3 justify-center">
                      {ACTIVITIES.filter(type => !type.hidden).map((activity) => (
                        <button
                          key={activity.id}
                          onClick={() => handleAddActivity(activity.id as ActivityType)}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-pointer"
                        >
                          {activity.icon}
                          <span className="text-gray-600">{t(activity.labelKey as any)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
      {modalCallbacks.renderModal()}
      <SaveToLibraryModal
        isOpen={activityToSaveToLibrary !== null}
        onClose={() => setActivityToSaveToLibrary(null)}
        activity={activityToSaveToLibrary}
      />
    </>
  );
};

export default DayBuilderStep;
